import React, { useState } from 'react';

/**
 * Lightweight HTML5 drag-and-drop list reordering.
 * Usage:
 *  <SortableList items={items} onReorder={(newOrder)=>...} renderItem={(item, dragHandle)=> ...} />
 * The renderItem receives a dragHandle prop with onMouseDown etc. — to make a part draggable
 * we use HTML5 draggable on the wrapping div.
 */
export const SortableList = ({ items, onReorder, renderItem, idKey = 'id' }) => {
  const [draggingId, setDraggingId] = useState(null);

  const handleDragStart = (e, id) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    if (!draggingId || draggingId === targetId) return;
    const oldIndex = items.findIndex((i) => i[idKey] === draggingId);
    const newIndex = items.findIndex((i) => i[idKey] === targetId);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = [...items];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);
    onReorder(reordered);
    setDraggingId(null);
  };

  return (
    <div className="space-y-2">
      {items.map((item) => (
        <div
          key={item[idKey]}
          draggable
          onDragStart={(e) => handleDragStart(e, item[idKey])}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, item[idKey])}
          onDragEnd={() => setDraggingId(null)}
          className={`transition-opacity ${draggingId === item[idKey] ? 'opacity-40' : ''}`}
          data-testid={`sortable-item-${item[idKey]}`}
        >
          {renderItem(item)}
        </div>
      ))}
    </div>
  );
};

export default SortableList;
